import { defineStore } from 'pinia';
import { useSessionStorage } from '@vueuse/core';
import * as logger from '../utils/logger';
import { useJobDataStore } from './job-store';
import { fetchCriticalApplicationFormData } from '../services/criticalApplicationData';
import { useProfile } from './profile-store';
// eslint-disable-next-line import/no-cycle
import { useApplicationStore } from './application-store';
import { useGdprTextsStore } from './gdpr-texts-store';

export const usePayloadStore = defineStore('payload', () => {
    const successfullyResolved = useSessionStorage<boolean>('lokarb:payload.exists', false);
    const atsUserUuid = useSessionStorage<string | null>('lokarb:payload.atsUserUuid', null);
    const encryptedPayload = useSessionStorage<string | null>('lokarb:payload.encryptedPayload', null);

    function $reset() {
        successfullyResolved.value = false;
        encryptedPayload.value = null;
        atsUserUuid.value = null;
    }

    async function decryptPayload(requestData: { uuid: string; payload: string }) {
        try {
            const { data } = await fetchCriticalApplicationFormData(requestData.uuid, requestData.payload);

            const jobData = useJobDataStore();

            const {
                customFields, job, company, gdprTexts, profile,
            } = data;

            // Set job-related data
            jobData.setCustomFields(customFields);
            jobData.$patch({ ...job, jobId: job.id });
            jobData.$patch({ company });

            // GDPR Texts
            const gdprTextsStore = useGdprTextsStore();
            gdprTextsStore.setTexts(gdprTexts);

            // Profile & Application
            if (profile) {
                const profileStore = useProfile();
                const application = useApplicationStore();
                profileStore.setProfile(profile);
                application.set(profile);
            }

            // Store for re-use
            successfullyResolved.value = true;
            encryptedPayload.value = requestData.payload;
            atsUserUuid.value = requestData.uuid;
        } catch (exception) {
            logger.warning({
                exception,
                message: 'Payload decryption failed.',
            });
            $reset();
        }
    }

    return {
        successfullyResolved,
        atsUserUuid,
        encryptedPayload,
        decryptPayload,
        $reset,
    };
});
